import React, { useRef, useEffect, useState, useContext } from 'react';
import Styled from 'styled';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { RouteComponentProps } from 'react-router';

import { Site } from 'four-nets/site';
import { UserStore } from 'four-nets/user/user';
import TitleView from 'four-nets/ui/title-view';
import { FourNetsApi } from 'four-nets/api/api';
import { ViewerContext } from 'four-nets/user/viewer/viewer';
import Unauthorized from 'four-nets/errors/401/unauthorized';
import newArticle from 'four-nets/icons/svg/icon_new-article.svg';
import { HideForUnpaidVendor } from 'four-nets/vendor/permissions';
import BigButtonView from 'four-nets/ui/big-button/big-button-view';
import { BazaarStore, SuperCategoryStore } from 'four-nets/bazaar/bazaar';
import BazaarProfileLoader from 'four-nets/bazaar/profile/bazaar-profile-loader';
import MyBazaarFilterView from 'four-nets/bazaar/my-bazaar/my-bazaar-filter-view';
import CreateEditProductView from 'four-nets/bazaar/product/create-edit-product/create-edit-product-view';
import { CreateEditProductViewModel } from 'four-nets/bazaar/product/create-edit-product/create-edit-product-view';

import { MyProductsStatusType, MyBazaarType } from './my-bazaar-table/query.graphql.types';
import MyTableView from './my-bazaar-table/my-bazaar-table';
import defaultStyles from './my-bazaar-loader.scss';
import titleStyles from './title-view.scss';

interface Search {
    page?: number;
    search?: string;
    excludeDeleted?: boolean;
    status?: string;
}

export interface Props extends RouteComponentProps {
    styles: typeof defaultStyles;
    search: Search;
    myBazaarType: MyBazaarType;
}

const MyBazaarLoader: React.FC<Props> = (props: Props, context) => {
    const {
        styles,
        myBazaarType = MyBazaarType.SELLING,
        search: { page, status: searchStatus },
    } = props;

    const ref = useRef<any>(null);
    const { viewer } = useContext(ViewerContext);
    const [bazaar, setBazaar] = useState(null);

    const status: MyProductsStatusType | undefined = searchStatus
        ? MyProductsStatusType[searchStatus.toUpperCase() as keyof typeof MyProductsStatusType]
        : MyProductsStatusType.ALL_WITHOUT_DELETED;

    const { api, site, userStore } = context;

    const superCategoryStore = new SuperCategoryStore({ api, userStore });
    const bazaarStore = new BazaarStore({ api, userStore });
    const createEditProductViewModel = new CreateEditProductViewModel({
        productStore: bazaarStore.productStore,
        superCategoryStore,
        site,
    });

    useEffect(() => {
        bazaarStore.loadMyBazaar().then((bazaar: any) => {
            setBazaar(bazaar);
        });
    }, []);

    if (!viewer) {
        return <Unauthorized />;
    }

    return (
        <div className={styles.pageWrapper}>
            <TitleView
                title={
                    <h1 className={styles.heading}>
                        <FormattedMessage id='HEADING_MY' />
                    </h1>
                }
                styles={titleStyles}
                rightComponent={
                    <HideForUnpaidVendor>
                        <BigButtonView
                            icon={newArticle}
                            label={<FormattedMessage id='BAZAAR_ADD_PRODUCT' />}
                            onClick={() => {
                                if (ref.current) {
                                    ref!.current!.show({
                                        form: createEditProductViewModel.setupForm(),
                                    });
                                }
                            }}
                        />
                    </HideForUnpaidVendor>
                }
            />
            <BazaarProfileLoader
                shopInfoTitle={<FormattedMessage id='BAZAAR_MY_RULES' />}
                noShopInfo={<FormattedMessage id='BAZAAR_NO_SHOP_INFO' />}
                user={{ username: viewer.username }}
            />
            <CreateEditProductView
                innerRef={(innerRef: any) => {
                    ref.current = innerRef;
                }}
                createEditProductViewModel={createEditProductViewModel}
            />

            <MyBazaarFilterView bazaar={bazaar} type={myBazaarType} />
            <MyTableView
                status={status}
                page={page || 1}
                myBazaarType={myBazaarType}
                forceUpdate={() => location.reload()}
            />
        </div>
    );
};

MyBazaarLoader.contextTypes = {
    api: PropTypes.instanceOf(FourNetsApi),
    site: PropTypes.instanceOf(Site),
    userStore: PropTypes.instanceOf(UserStore),
};

export default Styled(defaultStyles)(MyBazaarLoader);
